import { cx } from '@emotion/css'
import { Box, Tab as MuiTab } from '@mui/material'
import { arrayOf, bool, node, number, object, oneOf, oneOfType, shape, string } from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Tabs as StyledTabs } from './tabs.styled'

const TabPanel = (props) => {
	const { children, index, value, aboveTheFold, ...other } = props

	return (
		<div
			className={cx('TabPanel', value !== index ? 'hidden' : 'active')}
			aria-labelledby={`simple-tab-${index}`}
			hidden={index !== value}
			id={`simple-tabpanel-${index}`}
			role="tabpanel"
			style={{ height: aboveTheFold ? '100%' : undefined }}
			{...other}
		>
			{React.Children.map(children, (child) => {
				if (child?.props?.data) {
					child.props.data.aboveTheFold = aboveTheFold
				}
				return child
			})}
		</div>
	)
}

TabPanel.propTypes = {
	children: node,
	index: oneOfType([number, string]).isRequired,
	value: oneOfType([number, string]).isRequired,
}

const a11yProps = (index) => {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	}
}

const Tabs = ({
	aboveTheFold,
	className,
	switchMode,
	items,
	defaultValue,
	onChange,
	value: _value,
	alignment,
	css,
	sx,
	...props
}) => {
	const [value, setValue] = useState(_value ?? (defaultValue || items[0]?.name || 0))

	const handleChange = (event, newValue) => {
		setValue(newValue)
		if (onChange) onChange(newValue)
	}

	useEffect(() => {
		if (_value !== undefined) {
			setValue(_value)
		}
	}, [_value])

	return (
		<Box className={className} css={css} sx={{ ...sx, height: aboveTheFold ? '100%' : undefined }}>
			<StyledTabs {...props} onChange={handleChange} value={value} alignment={alignment}>
				{/* eslint-disable-next-line no-unused-vars */}
				{items.map(({ content, name, aboveTheFold, ...item }) => (
					<MuiTab {...item} {...a11yProps(name)} key={name} value={name} />
				))}
			</StyledTabs>

			{switchMode === 'hidden'
				? items.map(({ name, content, aboveTheFold }, index) => (
						<TabPanel key={index} index={name} value={value} aboveTheFold={aboveTheFold}>
							{content}
						</TabPanel>
				  ))
				: items.map(({ name, content, aboveTheFold }, index) => {
						if (name === value) {
							return (
								<TabPanel key={index} index={name} value={value} aboveTheFold={aboveTheFold}>
									{content}
								</TabPanel>
							)
						}
						return null
				  })}
		</Box>
	)
}

Tabs.defaultProps = {
	...StyledTabs.defaultProps,
	switchMode: 'hidden',
}

Tabs.propTypes = {
	...StyledTabs.propTypes,
	/**
	 * Default open tab
	 */
	defaultValue: oneOfType([number, string]),
	/**
	 * Items
	 */
	items: arrayOf(
		shape({
			className: string,
			content: node,
			disabled: bool,
			label: node,
			name: oneOfType([number, string]),
			style: object,
		})
	).isRequired,
	/**
	 * Load other tabs contents when viewing a tab, or not. View DOM to see the difference
	 */
	switchMode: oneOfType([oneOf(['hidden', 'unmount']), string]),
}

export default Tabs
